import React, { useState, useRef, useEffect } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { preventDefault } from '../../helpers'
import { eventHandler } from '../../types.d'
import BrailleLogo from './BrailleLogo'

export type LoginFrameChildrenProps = {
  recaptchaPromise?: () => Promise<string | null>
  recaptchaLoading?: boolean
}

type LoginFrameProps = {
  children: React.ReactNode
  title?: string
  recaptchaSiteKey?: string | null
  disableError?: boolean
  errorHandler?: (error: string) => React.ReactNode | string
  onPreviousStep?: eventHandler
  loading?: boolean
  error?: string | null
  logoUrl?: string | null
}

export function LoginFrame({
  children,
  title = 'Sign in to your account',
  recaptchaSiteKey = null,
  disableError = false,
  errorHandler = null,
  onPreviousStep = null,
  loading = false,
  error = null,
  logoUrl = null,
}: LoginFrameProps) {
  const recaptcha = recaptchaSiteKey !== null && recaptchaSiteKey !== ''
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const [recaptchaLoading, setRecaptchaLoading] = useState(recaptcha)
  const [verifyInProgress, setVerifyInProgress] = useState(false)

  const promiseRef = useRef<{
    resolve: (t: string | null) => void
    reject: (err: Error) => void
  } | null>(null)

  const prevVisibleRef = useRef(0)

  useEffect(() => {
    if (!recaptcha) return

    const handleDomChange = () => {
      const frames = document.querySelectorAll(
        'iframe[src*="recaptcha/api2/bframe"]',
      )
      const containers = Array.from(frames).map(
        (f) => f.parentElement?.parentElement as HTMLElement,
      )
      const visible = containers.filter(
        (el) => el?.style.visibility === 'visible',
      ).length

      if (visible < prevVisibleRef.current && verifyInProgress) {
        promiseRef.current?.reject(
          new Error('You cancelled the reCAPTCHA, please try again.'),
        )
        promiseRef.current = null
        setVerifyInProgress(false)
      }
      prevVisibleRef.current = visible
    }

    const obs = new MutationObserver(handleDomChange)
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    })
    return () => obs.disconnect()
  }, [recaptcha, verifyInProgress])

  useEffect(() => {
    return () => {
      recaptchaRef.current?.reset()
      promiseRef.current?.reject(
        new Error(
          'The reCAPTCHA was unexpectedly unmounted, please try again, or refresh the page.',
        ),
      )
      promiseRef.current = null
      setVerifyInProgress(false)
    }
  }, [])

  const recaptchaPromise = () => {
    if (!recaptcha) return Promise.resolve(null)

    if (verifyInProgress) recaptchaRef.current?.reset()

    return new Promise<string | null>((resolve, reject) => {
      promiseRef.current = { resolve, reject }
      setVerifyInProgress(true)
      recaptchaRef.current?.execute()
    })
  }

  const handleToken = (token: string | null) => {
    if (promiseRef.current) {
      promiseRef.current.resolve(token)
      promiseRef.current = null
    }
    setVerifyInProgress(false)
  }

  const handleError = () => {
    if (promiseRef.current) {
      promiseRef.current.reject(
        new Error(
          'There was an error with the reCAPTCHA, please try again, or refresh the page.',
        ),
      )
      promiseRef.current = null
    }
    setVerifyInProgress(false)
  }

  const sectionRef = useRef(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  const checkHeight = () => {
    const viewportHeight = window.innerHeight
    const sectionHeight = sectionRef.current?.offsetHeight || 0
    setIsOverflowing(sectionHeight > viewportHeight)
  }

  useEffect(() => {
    checkHeight()
    window.addEventListener('resize', checkHeight)
    return () => window.removeEventListener('resize', checkHeight)
  }, [])

  return (
    <>
      {recaptcha && (
        <ReCAPTCHA
          sitekey={recaptchaSiteKey}
          size="invisible"
          ref={recaptchaRef}
          asyncScriptOnLoad={() => setRecaptchaLoading(false)}
          onChange={handleToken}
          onExpired={() => handleToken(null)}
          onErrored={handleError}
          isolated={true}
        />
      )}
      <section
        className="flex flex-col items-center sm:justify-center px-2 py-2 sm:px-4 mx-auto lg:py-0 sm:h-screen"
      >
        <div
          ref={sectionRef}
          className="flex flex-col items-center sm:justify-center w-full"
        >
          {!isOverflowing && (
            logoUrl ? (
              <img
                className="w-20 h-20 sm:w-32 sm:h-32 mb-6"
                src={logoUrl}
                alt="logo"
              />
            ) : (
              <BrailleLogo className="w-16 h-16 sm:w-24 sm:h-24 mb-6" />
            )
          )}
          <div className="relative w-full bg-white rounded-lg shadow sm:mt-0 sm:max-w-md xl:p-0">
            {onPreviousStep && (
              <button
                onClick={preventDefault(onPreviousStep)}
                disabled={loading}
                type="button"
                id="previous-step"
                aria-label="Previous step"
                className={
                  'absolute float-left text-gray-400 hover:text-gray-600 font-medium rounded-lg text-sm p-2.5 text-center inline-flex items-center' +
                  (loading ? ' opacity-10 pointer-events-none' : '')
                }
              >
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 12h14M5 12l4-4m-4 4 4 4"
                  />
                </svg>
              </button>
            )}
            <div
              className={
                'px-4 py-6 space-y-4 sm:space-y-6 sm:p-8' +
                (onPreviousStep ? ' mt-3' : '') +
                (loading ? ' opacity-10 pointer-events-none' : '')
              }
            >
              {title && (
                <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-900 sm:text-2xl">
                  {title}
                </h1>
              )}
              <form className="space-y-4 sm:space-y-6">
                {!disableError && error && (
                  <div
                    className="font-medium p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50"
                    role="alert"
                  >
                    {errorHandler ? errorHandler(error) : error}
                  </div>
                )}
                {React.Children.map(children, (child) => {
                  if (!React.isValidElement(child)) return child
                  if (typeof child.type === 'function') {
                    return React.cloneElement(
                      child as React.ReactElement<any>,
                      {
                        recaptchaPromise,
                        recaptchaLoading,
                      },
                    )
                  }
                  return child
                })}
              </form>
            </div>
            {loading && (
              <div
                role="status"
                className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2"
              >
                <svg
                  aria-hidden="true"
                  className="w-8 h-8 text-gray-200 animate-spin fill-blue-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                <span className="sr-only">Loading...</span>
              </div>
            )}
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-600">
          Powered by <a href="https://tidyhq.com">TidyHQ</a> &bull;{' '}
          <a href="https://tidyhq.com/terms-of-service/">Terms</a>
        </p>
        {recaptcha && (
          <p className="mt-3 text-xs text-gray-400 text-center">
            TidyHQ is protected by reCAPTCHA and the
            <br />
            Google{' '}
            <a href="https://policies.google.com/privacy">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="https://policies.google.com/terms">Terms of Service</a>{' '}
            apply.
          </p>
        )}
      </section>
    </>
  )
}

export default LoginFrame
